import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { FieldLabel } from "../shared/FieldHint";
import { formatET } from "../../lib/dateUtils";
import { api, API, formatApiErrorDetail } from "../../lib/api";
import { useEditConfirm } from "../../context/EditConfirmContext";
import { DownloadSimple } from "@phosphor-icons/react";

const STATUSES = ["Submitted", "In Review", "Approved", "Funded", "Commission Paid"];

export function AffiliateFundingCommissionsTab({ managers }) {
  const confirmEdit = useEditConfirm();
  const [deals, setDeals] = useState([]);
  const [summary, setSummary] = useState(null);
  const [affiliates, setAffiliates] = useState([]);
  const [filters, setFilters] = useState({ manager_id: "all", status: "all" });
  const [form, setForm] = useState({ client_id: "", referring_type: "manager", referring_id: "", deal_amount: "", igc_commission_amount: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  const fetchAll = async () => {
    try {
      const params = {};
      if (filters.manager_id !== "all") params.manager_id = filters.manager_id;
      if (filters.status !== "all") params.status = filters.status;
      const [dealsRes, summaryRes] = await Promise.all([
        api.get("/admin/affiliate-funding-commissions", { params }),
        api.get("/admin/affiliate-funding-commissions/summary"),
      ]);
      setDeals(dealsRes.data);
      setSummary(summaryRes.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    api.get("/admin/affiliate-tracker").then(({ data }) => setAffiliates(data)).catch(() => {});
  }, []);

  useEffect(() => { fetchAll(); }, [filters.manager_id, filters.status]);

  const referringOptions = form.referring_type === "manager" ? managers : affiliates.map((a) => ({ id: a.id, code: a.affiliate_code, display_name: a.display_name }));

  const createDeal = async (e) => {
    e.preventDefault();
    if (!form.client_id.trim() || !form.referring_id || !form.deal_amount) { toast.error("Client, referrer, and deal amount are required"); return; }
    setSubmitting(true);
    try {
      await api.post("/admin/affiliate-funding-commissions", { ...form, deal_amount: parseFloat(form.deal_amount) || 0, igc_commission_amount: parseFloat(form.igc_commission_amount) || 0 });
      toast.success("Deal added to the pipeline");
      setForm({ client_id: "", referring_type: "manager", referring_id: "", deal_amount: "", igc_commission_amount: "", notes: "" });
      fetchAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const updateStatus = async (id, status) => {
    const d = deals.find((x) => x.id === id);
    if (!(await confirmEdit(`${d?.client_id || "this deal"}'s status`))) return;
    try {
      await api.patch(`/admin/affiliate-funding-commissions/${id}`, { status });
      toast.success("Deal updated");
      fetchAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="affiliate-funding-commissions-tab">
      <div className="flex justify-end mb-3">
        <a href={`${API}/admin/affiliate-funding-commissions/export-csv`} data-testid="afc-export-csv-button" title="Downloads a spreadsheet (CSV file) of every funding commission deal shown below, including amounts owed and paid — useful for running payroll or bookkeeping.">
          <Button variant="outline" className="border-igc-gold/30 text-igc-goldText h-9 text-xs"><DownloadSimple size={14} className="mr-1.5" /> Export Payout CSV</Button>
        </a>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
        <div className="bg-igc-surface border border-igc-gold/15 p-4" data-testid="afc-summary-in-pipeline">
          <FieldLabel hint="Deals not yet Funded or Commission Paid." testId="afc-summary-in-pipeline-hint" className="mb-1 block">In Pipeline</FieldLabel>
          <p className="font-serif text-xl text-igc-cream">{summary?.total_deals_in_pipeline ?? "—"}</p>
        </div>
        <div className="bg-igc-surface border border-igc-gold/15 p-4" data-testid="afc-summary-funded-month">
          <FieldLabel hint="Total deal amount that hit Funded status this calendar month." testId="afc-summary-funded-month-hint" className="mb-1 block">Funded This Month</FieldLabel>
          <p className="font-serif text-xl text-igc-success">${(summary?.total_funded_this_month ?? 0).toLocaleString()}</p>
        </div>
        <div className="bg-igc-surface border border-igc-gold/15 p-4" data-testid="afc-summary-owed">
          <FieldLabel hint="Affiliate + manager commission owed on Funded deals, not yet marked Commission Paid." testId="afc-summary-owed-hint" className="mb-1 block">Commissions Owed</FieldLabel>
          <p className="font-serif text-xl text-igc-goldText">${(summary?.total_commissions_owed ?? 0).toLocaleString()}</p>
        </div>
        <div className="bg-igc-surface border border-igc-gold/15 p-4" data-testid="afc-summary-paid-ytd">
          <FieldLabel hint="Total commissions marked Commission Paid so far this year." testId="afc-summary-paid-ytd-hint" className="mb-1 block">Paid YTD</FieldLabel>
          <p className="font-serif text-xl text-igc-cream">${(summary?.total_commissions_paid_ytd ?? 0).toLocaleString()}</p>
        </div>
        <div className="bg-igc-surface border border-igc-gold/15 p-4" data-testid="afc-summary-pipeline-value">
          <FieldLabel hint="Estimated total value of every deal still open — manager lead-inbox estimates plus open funding deals." testId="afc-summary-pipeline-value-hint" className="mb-1 block">Est. Pipeline Value</FieldLabel>
          <p className="font-serif text-xl text-igc-cream">${(summary?.total_estimated_pipeline_value ?? 0).toLocaleString()}</p>
        </div>
      </div>

      <h2 className="font-serif text-xl text-igc-cream tracking-tight mb-4">Add a Funding Deal</h2>
      <form onSubmit={createDeal} className="flex items-end gap-2 flex-wrap mb-4 bg-igc-surface border border-igc-gold/15 p-4">
        <Input data-testid="afc-client-id-input" placeholder="Client ID / name" title="The name or ID of the client this business funding deal belongs to." value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream w-40 h-9 text-xs" />
        <Select value={form.referring_type} onValueChange={(v) => setForm({ ...form, referring_type: v, referring_id: "" })}>
          <SelectTrigger data-testid="afc-referring-type-select" title="Choose whether this deal was referred by a Manager or a regular Affiliate rep." className="w-36 h-9 bg-igc-surfaceLight border-igc-gold/20 text-igc-cream text-xs"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
            <SelectItem value="manager">Manager</SelectItem>
            <SelectItem value="affiliate">Affiliate</SelectItem>
          </SelectContent>
        </Select>
        <Select value={form.referring_id} onValueChange={(v) => setForm({ ...form, referring_id: v })}>
          <SelectTrigger data-testid="afc-referring-id-select" title="Pick the specific manager or affiliate who referred this deal — they'll earn the commission below." className="w-52 h-9 bg-igc-surfaceLight border-igc-gold/20 text-igc-cream text-xs"><SelectValue placeholder="Referring party" /></SelectTrigger>
          <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
            {referringOptions.map((r) => <SelectItem key={r.id} value={r.id}>{r.code} — {r.display_name}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1">
          <Input data-testid="afc-deal-amount-input" type="number" min="0" placeholder="Deal amount" value={form.deal_amount} onChange={(e) => setForm({ ...form, deal_amount: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream w-32 h-9 text-xs" />
          <FieldLabel hint="The total funding amount the client is receiving." testId="afc-deal-amount-hint" />
        </div>
        <div className="flex items-center gap-1">
          <Input data-testid="afc-igc-commission-input" type="number" min="0" placeholder="IGC commission" value={form.igc_commission_amount} onChange={(e) => setForm({ ...form, igc_commission_amount: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream w-32 h-9 text-xs" />
          <FieldLabel hint="What IGC earns on this deal. Affiliate (18%) and manager override (2%) are calculated from this amount when the deal is marked Funded." testId="afc-igc-commission-hint" />
        </div>
        <Input data-testid="afc-notes-input" placeholder="Notes" title="Optional free-text notes about this deal, visible only to admins." value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream w-40 h-9 text-xs" />
        <Button data-testid="afc-create-deal-button" type="submit" disabled={submitting} title="Adds this deal to the funding commissions pipeline below." className="h-9 text-xs bg-igc-gold hover:bg-igc-goldHover text-black">Add Deal</Button>
      </form>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <Select value={filters.manager_id} onValueChange={(v) => setFilters({ ...filters, manager_id: v })}>
          <SelectTrigger data-testid="afc-filter-manager" title="Filter the list below to only deals referred by one specific manager." className="w-44 h-9 bg-igc-surfaceLight border-igc-gold/20 text-igc-cream text-xs"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
            <SelectItem value="all">All Managers</SelectItem>
            {managers.map((m) => <SelectItem key={m.id} value={m.id}>{m.code} — {m.display_name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
          <SelectTrigger data-testid="afc-filter-status" title="Filter the list below to only deals currently at one specific pipeline stage." className="w-40 h-9 bg-igc-surfaceLight border-igc-gold/20 text-igc-cream text-xs"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
            <SelectItem value="all">All Statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {deals.length === 0 && <p className="text-igc-muted text-sm" data-testid="afc-empty-state">No funding deals match this filter yet.</p>}
      <div className="space-y-2">
        {deals.map((d) => (
          <div key={d.id} className="bg-igc-surface border border-igc-gold/15 p-4 flex items-center justify-between gap-4 flex-wrap" data-testid={`afc-deal-row-${d.id}`}>
            <div>
              <p className="text-igc-cream text-sm">{d.client_id} <span className="text-igc-muted text-xs">via {d.referring_code} ({d.referring_type})</span></p>
              <p className="text-igc-muted text-xs">Deal ${d.deal_amount.toLocaleString()} — IGC commission ${d.igc_commission_amount.toLocaleString()} — Submitted {formatET(d.date_submitted, "MMM d, yyyy")}</p>
              {d.status === "Funded" || d.status === "Commission Paid" ? (
                <p className="text-igc-success text-xs mt-0.5" data-testid={`afc-deal-owed-${d.id}`}>
                  Affiliate owed ${d.affiliate_commission_owed.toLocaleString()}{d.manager_override_owed > 0 && ` + manager override $${d.manager_override_owed.toLocaleString()}`}
                  {d.payment_due_date && ` — due ${formatET(d.payment_due_date, "MMM d, yyyy")}`}
                </p>
              ) : null}
            </div>
            <Select value={d.status} onValueChange={(v) => updateStatus(d.id, v)}>
              <SelectTrigger data-testid={`afc-deal-status-select-${d.id}`} title="Move this deal to a new pipeline stage. Marking it Funded automatically calculates and records the affiliate/manager commissions owed." className="w-40 h-9 bg-igc-surfaceLight border-igc-gold/20 text-igc-cream text-xs"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        ))}
      </div>
    </div>
  );
}
